import { useState } from "react";
import { Icon } from "../../components/Icon.tsx";
import { Button, Field, Skeleton, Switch } from "../../components/ui.tsx";
import { t, type MessageKey } from "../../i18n/index.ts";
import { action } from "../../lib/api.ts";
import { parseMoney } from "../../lib/format.ts";
import { ManageLayout, useAction, useSave } from "./common.tsx";

interface Settings {
  id: string | null;
  tip_mode: "own" | "pool";
  overtime_after: number;
  late_after: number;
  swap_approval: boolean;
  week_limit: number;
}
interface Person {
  id: string;
  name: string;
  username: string;
  roles: string[];
  hourly_rate: number;
  payroll_id: string | null;
  tips: boolean;
}

/** How tips are shared, when overtime and lateness start, and each person's pay rate and payroll number. */
export function TeamSetup() {
  const { data, reload } = useAction<{ settings: Settings; people: Person[] }>(
    "team.settings",
  );
  return (
    <ManageLayout title={t("manage.team")} subtitle={t("teamset.subtitle")}>
      {!data ? (
        <Skeleton h={300} r="var(--radius-lg)" />
      ) : (
        <div className="team-setup">
          <Rules settings={data.settings} onSaved={reload} />
          <People people={data.people} onSaved={reload} />
        </div>
      )}
    </ManageLayout>
  );
}

function Rules({
  settings,
  onSaved,
}: {
  settings: Settings;
  onSaved: () => void;
}) {
  const { busy, run } = useSave();
  const [mode, setMode] = useState(settings.tip_mode);
  const [overtime, setOvertime] = useState(
    String(settings.overtime_after / 60),
  );
  const [late, setLate] = useState(String(settings.late_after));
  const [approval, setApproval] = useState(settings.swap_approval);
  const [limit, setLimit] = useState(
    settings.week_limit ? String(settings.week_limit / 60) : "",
  );
  const limitMin = limit.trim()
    ? Math.round(Number(limit.replace(",", ".")) * 60)
    : 0;
  const overtimeMin = Math.round(Number(overtime.replace(",", ".")) * 60);
  const lateMin = Number(late);
  const errors = {
    overtime: !(overtimeMin >= 60 && overtimeMin <= 1440)
      ? t("teamset.e_overtime")
      : null,
    late: !(Number.isInteger(lateMin) && lateMin >= 0 && lateMin <= 120)
      ? t("teamset.e_late")
      : null,
    limit: !(limitMin >= 0 && limitMin <= 7 * 24 * 60)
      ? t("teamset.e_limit")
      : null,
  };
  const save = () =>
    void run(async () => {
      await action("team.save_settings", {
        tip_mode: mode,
        overtime_after: overtimeMin,
        late_after: lateMin,
        swap_approval: approval,
        week_limit: limitMin,
      });
      onSaved();
    }, t("teamset.saved"));
  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2>{t("teamset.tips_title")}</h2>
          <span className="muted">{t("teamset.tips_hint")}</span>
        </div>
      </header>
      <div
        className="team-modes"
        role="radiogroup"
        aria-label={t("teamset.tips_title")}
      >
        {(["own", "pool"] as const).map((m) => (
          <button
            key={m}
            role="radio"
            aria-checked={mode === m}
            className={`team-mode${mode === m ? " active" : ""}`}
            onClick={() => setMode(m)}
          >
            <Icon name={m === "own" ? "users" : "split"} size={22} />
            <strong>{t(`teamset.mode_${m}` as MessageKey)}</strong>
            <span className="muted">
              {t(`teamset.mode_${m}_hint` as MessageKey)}
            </span>
          </button>
        ))}
      </div>
      {mode !== settings.tip_mode ? (
        <p className="callout callout-info">
          <Icon name="alert" size={16} /> {t("teamset.mode_change")}
        </p>
      ) : null}
      <div className="form-grid">
        <Field
          label={t("teamset.overtime")}
          hint={t("teamset.overtime_hint")}
          error={errors.overtime}
        >
          <input
            className="input"
            inputMode="decimal"
            value={overtime}
            onChange={(e) => setOvertime(e.target.value)}
          />
        </Field>
        <Field
          label={t("teamset.late")}
          hint={t("teamset.late_hint")}
          error={errors.late}
        >
          <input
            className="input"
            inputMode="numeric"
            value={late}
            onChange={(e) => setLate(e.target.value.replace(/\D/g, ""))}
          />
        </Field>
        <Field
          label={t("teamset.limit")}
          hint={t("teamset.limit_hint")}
          error={errors.limit}
        >
          <input
            className="input"
            inputMode="decimal"
            placeholder={t("teamset.no_limit")}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
          />
        </Field>
      </div>
      <label className="switch-row">
        <span>{t("teamset.swap_approval")}</span>
        <Switch
          checked={approval}
          onChange={setApproval}
          label={t("teamset.swap_approval")}
        />
      </label>
      <p className="muted small">{t("teamset.swap_approval_hint")}</p>
      <div className="form-actions">
        <Button
          variant="primary"
          icon="check"
          busy={busy}
          disabled={!!(errors.overtime || errors.late || errors.limit)}
          onClick={save}
        >
          {t("common.save")}
        </Button>
      </div>
    </section>
  );
}

function People({
  people,
  onSaved,
}: {
  people: Person[];
  onSaved: () => void;
}) {
  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2>{t("teamset.people_title")}</h2>
          <span className="muted">{t("teamset.people_hint")}</span>
        </div>
      </header>
      <ul className="team-pay-list">
        {people.map((p) => (
          <PayRow key={p.id} person={p} onSaved={onSaved} />
        ))}
      </ul>
    </section>
  );
}

function PayRow({ person, onSaved }: { person: Person; onSaved: () => void }) {
  const { busy, run } = useSave();
  const [rate, setRate] = useState(
    person.hourly_rate ? String(person.hourly_rate / 100) : "",
  );
  const [pid, setPid] = useState(person.payroll_id ?? "");
  const [tips, setTips] = useState(person.tips);
  const parsed = rate.trim() ? parseMoney(rate) : 0;
  const dirty =
    parsed !== person.hourly_rate ||
    pid.trim() !== (person.payroll_id ?? "") ||
    tips !== person.tips;
  const save = () =>
    void run(
      async () => {
        await action("team.save_member", {
          user: person.id,
          hourly_rate: parsed,
          payroll_id: pid.trim() || null,
          tips,
        });
        onSaved();
      },
      t("teamset.person_saved", { name: person.name }),
    );
  return (
    <li className="team-pay-row">
      <span className="rota-person">
        <strong>{person.name}</strong>
        <span className="muted small">
          {person.roles.map((r) => t(`role.${r}` as MessageKey)).join(", ")}
        </span>
      </span>
      <Field
        label={t("teamset.rate")}
        error={parsed === null ? t("teamset.e_rate") : null}
      >
        <input
          className="input"
          inputMode="decimal"
          placeholder="0"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
        />
      </Field>
      <Field label={t("teamset.payroll_id")}>
        <input
          className="input"
          value={pid}
          maxLength={40}
          onChange={(e) => setPid(e.target.value)}
        />
      </Field>
      <Switch
        checked={tips}
        onChange={setTips}
        label={t("teamset.gets_tips")}
      />
      <Button
        variant={dirty ? "primary" : "ghost"}
        icon="check"
        busy={busy}
        disabled={!dirty || parsed === null}
        onClick={save}
      >
        {t("common.save")}
      </Button>
    </li>
  );
}
